import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, GripVertical, Loader2 } from "lucide-react";
import {
  DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, sortableKeyboardCoordinates, useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PersonLink } from "@/components/groups/PersonLink";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const QUESTION_TYPES = ["short_text", "long_text", "dropdown", "checkbox",
  "multi_checkbox", "radio", "rating", "number", "phone"];
const CHOICE_TYPES = ["dropdown", "radio", "multi_checkbox"];
const MAX_QUESTIONS = 7;
const PAGE = 25;

const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return ""; } };
export const answerText = (value) =>
  Array.isArray(value) ? (value.join(", ") || "—")
    : typeof value === "boolean" ? (value ? "Yes" : "No")
      : (value === null || value === undefined || value === "" ? "—" : String(value));

/** One draggable question row. Only the grip starts a drag, so typing in a field or
 *  toggling Required can never move a question (GRP-DEC-310). */
function QuestionRow({ q, index, total, onUpdate, onRemove, onMove }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition,
    isDragging } = useSortable({ id: q.key });
  return (
    <div ref={setNodeRef}
         style={{ transform: CSS.Transform.toString(transform), transition }}
         className={`space-y-2 rounded-lg border bg-card p-3 ${isDragging ? "z-10 shadow-lg" : ""}`}
         data-testid={`question-row-${index}`}>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" ref={setActivatorNodeRef} {...attributes} {...listeners}
                aria-label={`Reorder ${q.label || "question"}`}
                className="shrink-0 cursor-grab touch-none rounded p-1 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:cursor-grabbing"
                data-testid={`question-drag-${index}`}>
          <GripVertical className="h-4 w-4" />
        </button>
        <Input className="min-w-[180px] flex-1" value={q.label} placeholder="Question"
               maxLength={200} onChange={(e) => onUpdate({ label: e.target.value })}
               data-testid={`question-label-${index}`} />
        <Select value={q.type} onValueChange={(v) => onUpdate({ type: v })}>
          <SelectTrigger className="w-[170px]" data-testid={`question-type-${index}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {QUESTION_TYPES.map((t) => (
              <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-sm">
          <Switch checked={Boolean(q.required)} data-testid={`question-required-${index}`}
                  onCheckedChange={(v) => onUpdate({ required: v })} />Required
        </label>
        {/* Accessible fallback for the drag interaction — keyboard and assistive-tech
            users reorder with these; the primary experience stays drag-and-drop. */}
        <div className="flex items-center">
          <button type="button" aria-label="Move question up" disabled={index === 0}
                  onClick={() => onMove(-1)}
                  className="rounded p-1 text-muted-foreground transition-colors hover:text-foreground disabled:opacity-30"
                  data-testid={`question-move-up-${index}`}>
            <ChevronUp className="h-4 w-4" />
          </button>
          <button type="button" aria-label="Move question down" disabled={index === total - 1}
                  onClick={() => onMove(1)}
                  className="rounded p-1 text-muted-foreground transition-colors hover:text-foreground disabled:opacity-30"
                  data-testid={`question-move-down-${index}`}>
            <ChevronDown className="h-4 w-4" />
          </button>
        </div>
        <Button variant="ghost" size="sm" data-testid={`question-remove-${index}`}
                onClick={onRemove}>Remove</Button>
      </div>
      {CHOICE_TYPES.includes(q.type) ? (
        <Input value={(q.choices || []).join(", ")} placeholder="Choices, comma separated"
               onChange={(e) => onUpdate({
                 choices: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
               })} data-testid={`question-choices-${index}`} />
      ) : null}
    </div>
  );
}

function QuestionsEditor({ groupId, initial, onSaved }) {
  const seed = React.useCallback(
    () => (initial?.questions || []).map((q, i) => ({ ...q, key: q.id || `new-${i}` })), [initial]);
  const [items, setItems] = React.useState(seed);
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => { setItems(seed()); }, [seed]);

  const sensors = useSensors(
    // A small distance threshold means a tap/click on the grip is not a drag, and a
    // touch drag does not fight the page's own scrolling.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const savedIds = (initial?.questions || []).map((q) => q.id);
  /** Order alone is persisted immediately when every question already exists on the
   *  server; otherwise it travels with Save (the set itself is changing anyway). */
  const persistOrder = async (next) => {
    const ids = next.map((q) => q.id);
    if (ids.length !== savedIds.length || ids.some((id) => !id || !savedIds.includes(id))) return;
    try {
      await api.post(`/groups/${groupId}/questions/reorder`, { question_ids: ids });
      toast.success("Question order saved.");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save the new order.");
      setItems(seed());
    }
  };

  const move = (from, to) => {
    if (to < 0 || to >= items.length) return;
    const next = items.slice();
    next.splice(to, 0, next.splice(from, 1)[0]);
    setItems(next);
    persistOrder(next);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/groups/${groupId}/questions`, {
        questions: items.map(({ key, ...q }, i) => ({ ...q, order: i })),
      });
      toast.success("Questions saved as a new version.");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save questions.");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="group-questions-editor">
      <div>
        <h2 className="text-base font-semibold">Membership questions</h2>
        <p className="text-sm text-muted-foreground">
          Up to {MAX_QUESTIONS}. Everyone who joins this group answers them — whether the
          group is open, approval-based or invite-only. Drag a question by its handle to
          reorder it; existing members are never asked again.
        </p>
      </div>
      <DndContext sensors={sensors} collisionDetection={closestCenter}
                  onDragEnd={({ active, over }) => {
                    if (!over || active.id === over.id) return;
                    move(items.findIndex((q) => q.key === active.id),
                         items.findIndex((q) => q.key === over.id));
                  }}>
        <SortableContext items={items.map((q) => q.key)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {items.map((q, i) => (
              <QuestionRow key={q.key} q={q} index={i} total={items.length}
                           onMove={(delta) => move(i, i + delta)}
                           onUpdate={(patch) => setItems((all) => all.map(
                             (it, idx) => (idx === i ? { ...it, ...patch } : it)))}
                           onRemove={() => setItems((all) => all.filter((_, idx) => idx !== i))} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      {!items.length ? (
        <p className="text-sm text-muted-foreground" data-testid="group-questions-empty">
          No questions yet — members join without answering anything.
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" data-testid="question-add"
                disabled={items.length >= MAX_QUESTIONS}
                onClick={() => setItems((all) => [...all, {
                  key: `new-${Date.now()}`, type: "short_text", label: "",
                  required: false, choices: [],
                }])}>Add question</Button>
        <Button onClick={save} disabled={busy} data-testid="questions-save">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Save questions
        </Button>
      </div>
    </div>
  );
}

/** Group-centric answer review. Owner/Admin only, bounded page — a large group's whole
 *  answer history is never loaded into the browser (GRP-DEC-311). */
/**
 * GRP-DEC-314 — pending applicants, at the TOP of the Answers review, so an organizer can
 * read a response and decide in one place. It is NOT a second approval implementation:
 * the rows come from the canonical request store (`GET /groups/{id}/requests`, the one
 * surface that returns a pending applicant's answers) and the decisions call the SAME
 * canonical endpoints Manage → Requests calls, so authorization, auditing, notifications,
 * membership creation, answer retention, pending counts, decline reasons and the
 * declined-request block all behave identically.
 */
function PendingApplicants({ groupId }) {
  const qc = useQueryClient();
  const [reasonFor, setReasonFor] = React.useState(null);
  const [reason, setReason] = React.useState("");
  const [busy, setBusy] = React.useState(null);
  const { data } = useQuery({
    queryKey: ["group-manage-requests", groupId],
    queryFn: async () => (await api.get(`/groups/${groupId}/requests`)).data,
    retry: false,
  });
  const rows = data?.requests || [];

  const decide = async (r, approve, why) => {
    setBusy(r.id);
    try {
      await api.post(`/groups/${groupId}/requests/${r.id}/${approve ? "approve" : "decline"}`,
        approve ? undefined : { reason: why || null });
      toast.success(approve ? "Member approved." : "Request declined.");
      // Everything that derives from a decision refreshes without a reload.
      ["group-manage-requests", "group-manage-members", "group-manage-answers",
        "group-manage"].forEach((k) => qc.invalidateQueries({ queryKey: [k, groupId] }));
      qc.invalidateQueries({ queryKey: ["groups-pending-request-count"] });
      qc.invalidateQueries({ queryKey: ["groups-new-member-count"] });
      qc.invalidateQueries({ queryKey: ["groups-mine"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "That didn't work.");
    } finally { setBusy(null); setReasonFor(null); setReason(""); }
  };

  if (!rows.length) return null;
  return (
    <div className="space-y-2" data-testid="group-answers-pending">
      <h3 className="text-sm font-[640]">
        Awaiting a decision
        <span className="ml-2 text-xs font-normal text-muted-foreground">
          {rows.length} applicant{rows.length === 1 ? "" : "s"}
        </span>
      </h3>
      {rows.map((r) => (
        <Card key={r.id} className="space-y-3 border-amber-200 bg-amber-50/40 p-4"
              data-testid={`group-answers-applicant-${r.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <PersonLink person={r.member} testId={`group-answers-applicant-link-${r.user_id}`}
                        subtitle={`Requested ${fmt(r.created_at)}${r.declined_count
                          ? ` · declined before: ${r.declined_count}` : ""}`} />
            {/* ONE action pair per applicant — never one beside each question. */}
            <div className="flex items-center gap-2">
              <Button size="sm" disabled={busy === r.id}
                      data-testid={`group-answers-approve-${r.id}`}
                      onClick={() => decide(r, true)}>Approve</Button>
              <Button size="sm" variant="outline" disabled={busy === r.id}
                      data-testid={`group-answers-decline-${r.id}`}
                      onClick={() => { setReasonFor(r.id); setReason(""); }}>Decline</Button>
            </div>
          </div>
          {(r.answers || []).length ? (
            <div className="space-y-1 rounded-lg border bg-card p-3"
                 data-testid={`group-answers-applicant-answers-${r.id}`}>
              {r.answers.map((a) => (
                <p key={a.question_id} className="text-sm">
                  <span className="font-medium">{a.label}: </span>
                  <span className="text-muted-foreground">{answerText(a.value)}</span>
                </p>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              This applicant answered no questions.
            </p>
          )}
          {reasonFor === r.id ? (
            <div className="space-y-2 rounded-lg border bg-card p-3">
              <Label htmlFor={`decline-reason-${r.id}`} className="text-xs">
                Reason (optional — shared with the applicant)
              </Label>
              <Textarea id={`decline-reason-${r.id}`} rows={2} value={reason}
                        maxLength={500} onChange={(e) => setReason(e.target.value)}
                        data-testid={`group-answers-decline-reason-${r.id}`} />
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={busy === r.id}
                        data-testid={`group-answers-decline-confirm-${r.id}`}
                        onClick={() => decide(r, false, reason)}>Confirm decline</Button>
                <Button size="sm" variant="ghost" onClick={() => setReasonFor(null)}
                        data-testid={`group-answers-decline-cancel-${r.id}`}>Cancel</Button>
              </div>
            </div>
          ) : null}
        </Card>
      ))}
    </div>
  );
}

function AnswersPanel({ groupId }) {
  const [offset, setOffset] = React.useState(0);
  const { data, isLoading } = useQuery({
    queryKey: ["group-manage-answers", groupId, offset],
    queryFn: async () => (await api.get(`/groups/${groupId}/answers`, {
      params: { limit: PAGE, offset },
    })).data,
  });
  const rows = data?.submissions || [];
  const total = data?.total || 0;

  return (
    <div className="space-y-3" data-testid="group-answers-panel">
      <div>
        <h2 className="text-base font-semibold">Answers</h2>
        <p className="text-sm text-muted-foreground">
          Every membership-question response collected by this group, newest first. Only
          organizers can see these.
        </p>
      </div>
      <PendingApplicants groupId={groupId} />
      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading answers…</p>
      ) : !rows.length ? (
        <p className="text-sm text-muted-foreground" data-testid="group-answers-empty">
          No answers submitted yet.
        </p>
      ) : rows.map((s) => (
        <Card key={s.id} className="space-y-2 p-4" data-testid={`group-answer-${s.id}`}>
          <div className="flex flex-wrap items-center gap-2">
            <PersonLink person={s.member} testId={`group-answer-person-${s.id}`} size={28} />
            <Badge variant="outline" data-testid={`group-answer-version-${s.id}`}>
              v{s.questions_version}
            </Badge>
            {s.membership_state ? (
              <Badge variant="secondary" className="capitalize">{s.membership_state}</Badge>
            ) : null}
            <span className="text-xs text-muted-foreground">
              via {String(s.via || "join").replace(/_/g, " ")} · {fmt(s.submitted_at)}
            </span>
          </div>
          <div className="space-y-1 rounded-lg border bg-muted/30 p-3">
            {(s.answers || []).map((a) => (
              <p key={a.question_id} className="text-sm">
                <span className="font-medium">{a.label}: </span>
                <span className="text-muted-foreground">{answerText(a.value)}</span>
              </p>
            ))}
          </div>
        </Card>
      ))}
      {total > PAGE ? (
        <div className="flex items-center gap-2" data-testid="group-answers-pager">
          <Button variant="outline" size="sm" disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - PAGE))}
                  data-testid="group-answers-prev">Previous</Button>
          <span className="text-xs text-muted-foreground">
            {offset + 1}–{Math.min(offset + PAGE, total)} of {total}
          </span>
          <Button variant="outline" size="sm" disabled={offset + PAGE >= total}
                  onClick={() => setOffset(offset + PAGE)}
                  data-testid="group-answers-next">Next</Button>
        </div>
      ) : null}
    </div>
  );
}

export function GroupQuestionsManager({ groupId, initial, onSaved }) {
  const [view, setView] = React.useState("questions");
  return (
    <Card className="border-0 space-y-4 p-5" data-testid="group-questions-panel">
      <Tabs value={view} onValueChange={setView}>
        <TabsList data-testid="group-questions-subtabs">
          <TabsTrigger value="questions" data-testid="group-questions-subtab-questions">
            Questions
          </TabsTrigger>
          <TabsTrigger value="answers" data-testid="group-questions-subtab-answers">
            Answers
          </TabsTrigger>
        </TabsList>
      </Tabs>
      {view === "questions" ? (
        <QuestionsEditor groupId={groupId} initial={initial} onSaved={onSaved} />
      ) : <AnswersPanel groupId={groupId} />}
    </Card>
  );
}
